// Gov Opportunity detail — solicitation + pipeline + proposal sections + teaming partners.
import { useState } from "react";
import type { ReactNode } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { BulkModal } from "@/components/bulk-modal";
import { Plus, Trash2 } from "lucide-react";

const GOV_STAGES = ["IDENTIFIED", "REVIEWING", "BID_NO_BID", "CAPTURE", "PROPOSAL", "SUBMITTED", "AWARDED", "LOST", "ARCHIVED"] as const;
const GOV_ROLES = ["PRIME", "SUB"] as const;
const NDA_STATUSES = ["NONE", "REQUESTED", "SIGNED", "EXPIRED"] as const;
const SECTION_STATUSES = ["NOT_STARTED", "IN_PROGRESS", "IN_REVIEW", "APPROVED", "FINAL"] as const;
const BID_DECISIONS = ["PENDING", "BID", "NO_BID"] as const;

const apiErr = (e: any) => e?.response?.data?.message ?? e?.response?.data?.error?.message ?? e?.message ?? "Something went wrong";
const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString() : null);
const lbl = (s: string) => s.replace(/_/g, " ");

function KV({ k, v }: { k: string; v: ReactNode }) {
  if (v === null || v === undefined || v === "" || v === false) return null;
  return (
    <div className="flex justify-between gap-4 border-b py-1.5 last:border-0">
      <span className="text-xs text-muted-foreground">{k}</span>
      <span className="text-right text-sm">{v}</span>
    </div>
  );
}

type Tab = "sections" | "teaming";

export default function GovOpportunityDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("sections");
  const queryKey = ["/gov-opportunities", id];
  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async () => (await api.get(`/gov-opportunities/${id}`)).data,
    enabled: !!id,
  });
  const refetch = () => qc.invalidateQueries({ queryKey });
  async function patch(body: Record<string, unknown>) { await api.put(`/gov-opportunities/${id}`, body); refetch(); }
  async function del() {
    if (!data || !confirm(`Delete opportunity "${data.title}"?`)) return;
    try {
      await api.delete(`/gov-opportunities/${id}`);
      qc.invalidateQueries({ queryKey: ["/gov-opportunities"] });
      navigate("/gov-opportunities");
    } catch (e) { alert(apiErr(e)); }
  }

  if (isLoading) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  if (!data) return <div className="p-6 text-sm text-muted-foreground">Not found.</div>;
  const o = data;

  return (
    <>
      <PageHeader title={o.title} description={`${o.agency} · ${o.solicitationNumber}`} actions={<Button variant="outline" onClick={del}><Trash2 className="mr-1 h-4 w-4" />Delete</Button>} />
      <div className="space-y-4 p-6">
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader><CardTitle className="text-base">Solicitation</CardTitle></CardHeader>
            <CardContent>
              <KV k="Solicitation #" v={<span className="font-mono text-xs">{o.solicitationNumber}</span>} />
              <KV k="Agency" v={`${o.agency} (${o.agencyType})`} />
              <KV k="NAICS" v={o.naicsCode} />
              <KV k="PSC" v={o.pscCode} />
              <KV k="Set-aside" v={o.setAsideType} />
              <KV k="Vehicle" v={o.contractVehicle} />
              <KV k="Est. value" v={o.estimatedValue != null ? `$${Number(o.estimatedValue).toLocaleString()}` : null} />
              <KV k="Due" v={fmtDate(o.responseDueDate)} />
              <KV k="SAM.gov" v={o.samGovUrl ? <a className="hover:underline" href={o.samGovUrl} target="_blank" rel="noreferrer">Listing</a> : null} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Pipeline</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-muted-foreground">Stage</span>
                <Select value={o.stage} onChange={(e) => patch({ stage: e.target.value })} className="h-7 w-40 text-xs">{GOV_STAGES.map((s) => <option key={s} value={s}>{lbl(s)}</option>)}</Select>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-muted-foreground">Bid decision</span>
                <Select value={o.bidDecision ?? "PENDING"} onChange={(e) => patch({ bidDecision: e.target.value })} className="h-7 w-40 text-xs">{BID_DECISIONS.map((s) => <option key={s} value={s}>{lbl(s)}</option>)}</Select>
              </div>
              <KV k="Role" v={o.role} />
              <KV k="Relevance" v={o.relevanceScore != null ? `${o.relevanceScore}/100` : null} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Capture notes</CardTitle></CardHeader>
            <CardContent>
              <KV k="Bid rationale" v={o.bidDecisionRationale} />
              {o.captureData && <pre className="mt-2 max-h-40 overflow-auto rounded bg-muted/40 p-2 text-xs">{JSON.stringify(o.captureData, null, 2)}</pre>}
              {!o.bidDecisionRationale && !o.captureData && <p className="text-sm text-muted-foreground">No capture notes.</p>}
            </CardContent>
          </Card>
        </div>
        <Card>
          <div className="flex gap-1 border-b px-2 pt-2">
            <button onClick={() => setTab("sections")} className={`rounded-t px-3 py-1.5 text-sm ${tab === "sections" ? "border-b-2 border-primary font-medium text-primary" : "text-muted-foreground"}`}>Proposal sections</button>
            <button onClick={() => setTab("teaming")} className={`rounded-t px-3 py-1.5 text-sm ${tab === "teaming" ? "border-b-2 border-primary font-medium text-primary" : "text-muted-foreground"}`}>Teaming partners</button>
          </div>
          <CardContent className="pt-4">
            {tab === "sections" && <SectionsPanel oppId={o.id} sections={o.proposalSections ?? []} onChange={refetch} />}
            {tab === "teaming" && <TeamingPanel oppId={o.id} teamings={o.teamings ?? []} onChange={refetch} />}
          </CardContent>
        </Card>
      </div>
    </>
  );
}

function SectionsPanel({ oppId, sections, onChange }: { oppId: string; sections: any[]; onChange: () => void }) {
  const [addOpen, setAddOpen] = useState(false);
  const users = useQuery({ queryKey: ["/users", "picker"], queryFn: async () => (await api.get("/users")).data.data as { id: string; name: string }[] });
  const userName = (uid?: string) => users.data?.find((u) => u.id === uid)?.name;
  async function setField(sid: string, body: Record<string, unknown>) { await api.put(`/gov-opportunities/sections/${sid}`, body); onChange(); }
  async function remove(sid: string) { if (!confirm("Delete this section?")) return; await api.delete(`/gov-opportunities/sections/${sid}`); onChange(); }
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">{sections.length} section{sections.length === 1 ? "" : "s"}</div>
        <Button size="sm" onClick={() => setAddOpen(true)}><Plus className="mr-1 h-4 w-4" />Add section</Button>
      </div>
      {sections.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">No proposal sections.</p>}
      {sections.length > 0 && (
        <div className="rounded-md border">
          <Table>
            <Thead><Tr><Th>Section</Th><Th>Owner</Th><Th>Reviewer</Th><Th>Status</Th><Th>Due</Th><Th className="w-10"></Th></Tr></Thead>
            <Tbody>
              {sections.map((s: any) => (
                <Tr key={s.id} className="group">
                  <Td className="font-medium">{s.sectionName}</Td>
                  <Td className="text-sm text-muted-foreground">{s.owner?.name ?? userName(s.assignedTo) ?? "—"}</Td>
                  <Td className="text-sm text-muted-foreground">{s.reviewer?.name ?? userName(s.reviewerId) ?? "—"}</Td>
                  <Td><Select value={s.status} onChange={(e) => setField(s.id, { status: e.target.value })} className="h-7 w-36 text-xs">{SECTION_STATUSES.map((x) => <option key={x} value={x}>{lbl(x)}</option>)}</Select></Td>
                  <Td className="text-sm">{fmtDate(s.dueDate) ?? "—"}</Td>
                  <Td><button onClick={() => remove(s.id)} className="text-destructive opacity-0 group-hover:opacity-100" aria-label="Remove"><Trash2 className="h-4 w-4" /></button></Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </div>
      )}
      {addOpen && <AddSectionModal oppId={oppId} users={users.data ?? []} onClose={() => setAddOpen(false)} onAdded={onChange} />}
    </div>
  );
}

function AddSectionModal({ oppId, users, onClose, onAdded }: { oppId: string; users: { id: string; name: string }[]; onClose: () => void; onAdded: () => void }) {
  const [sectionName, setSectionName] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [reviewerId, setReviewerId] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: async () => { await api.post(`/gov-opportunities/${oppId}/sections`, { sectionName, assignedTo: assignedTo || undefined, reviewerId: reviewerId || undefined, dueDate: dueDate || undefined }); },
    onSuccess: () => { onAdded(); onClose(); },
    onError: (e) => setErr(apiErr(e)),
  });
  return (
    <BulkModal open onClose={onClose} title="Add proposal section" confirmLabel="Add" confirmDisabled={!sectionName.trim()} busy={mut.isPending} error={err} onConfirm={() => mut.mutate()}>
      <div className="space-y-1"><Label>Section name</Label><Input value={sectionName} onChange={(e) => setSectionName(e.target.value)} placeholder="e.g. Technical Volume" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1"><Label>Owner</Label><Select value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)}><option value="">—</option>{users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</Select></div>
        <div className="space-y-1"><Label>Reviewer</Label><Select value={reviewerId} onChange={(e) => setReviewerId(e.target.value)}><option value="">—</option>{users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</Select></div>
      </div>
      <div className="space-y-1"><Label>Due date</Label><Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} /></div>
    </BulkModal>
  );
}

function TeamingPanel({ oppId, teamings, onChange }: { oppId: string; teamings: any[]; onChange: () => void }) {
  const [addOpen, setAddOpen] = useState(false);
  async function remove(tid: string) { if (!confirm("Remove this teaming partner?")) return; await api.delete(`/gov-opportunities/teaming/${tid}`); onChange(); }
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">{teamings.length} partner{teamings.length === 1 ? "" : "s"}</div>
        <Button size="sm" onClick={() => setAddOpen(true)}><Plus className="mr-1 h-4 w-4" />Add partner</Button>
      </div>
      {teamings.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">No teaming partners.</p>}
      {teamings.length > 0 && (
        <div className="rounded-md border">
          <Table>
            <Thead><Tr><Th>Partner</Th><Th>Role</Th><Th>Workshare</Th><Th>NDA</Th><Th className="w-10"></Th></Tr></Thead>
            <Tbody>
              {teamings.map((t: any) => (
                <Tr key={t.id} className="group">
                  <Td className="font-medium">{t.partnerAccount ? <Link to={`/accounts/${t.partnerAccount.id}`} className="hover:underline">{t.partnerAccount.name}</Link> : "—"}</Td>
                  <Td><Badge variant="secondary">{t.role}</Badge></Td>
                  <Td className="text-sm">{t.worksharePct != null ? `${t.worksharePct}%` : "—"}</Td>
                  <Td className="text-sm">{t.ndaStatus}</Td>
                  <Td><button onClick={() => remove(t.id)} className="text-destructive opacity-0 group-hover:opacity-100" aria-label="Remove"><Trash2 className="h-4 w-4" /></button></Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </div>
      )}
      {addOpen && <AddTeamingModal oppId={oppId} onClose={() => setAddOpen(false)} onAdded={onChange} />}
    </div>
  );
}

function AddTeamingModal({ oppId, onClose, onAdded }: { oppId: string; onClose: () => void; onAdded: () => void }) {
  const [q, setQ] = useState("");
  const [partnerAccountId, setPartnerAccountId] = useState("");
  const [role, setRole] = useState("SUB");
  const [worksharePct, setWorksharePct] = useState("");
  const [ndaStatus, setNdaStatus] = useState("NONE");
  const [err, setErr] = useState<string | null>(null);
  const accounts = useQuery({ queryKey: ["picker", "accounts", q], queryFn: async () => (await api.get("/accounts", { params: { q, pageSize: 25 } })).data.data as any[] });
  const list = accounts.data ?? [];
  const mut = useMutation({
    mutationFn: async () => { await api.post(`/gov-opportunities/${oppId}/teaming`, { partnerAccountId, role, worksharePct: worksharePct ? Number(worksharePct) : undefined, ndaStatus }); },
    onSuccess: () => { onAdded(); onClose(); },
    onError: (e) => setErr(apiErr(e)),
  });
  return (
    <BulkModal open onClose={onClose} title="Add teaming partner" confirmLabel="Add" confirmDisabled={!partnerAccountId} busy={mut.isPending} error={err} onConfirm={() => mut.mutate()}>
      <div className="space-y-1"><Label>Partner account</Label><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search accounts…" /></div>
      <div className="max-h-40 overflow-auto rounded-md border">
        {accounts.isFetching && <p className="p-2 text-sm text-muted-foreground">Loading…</p>}
        {!accounts.isFetching && list.length === 0 && <p className="p-2 text-sm text-muted-foreground">No matches.</p>}
        {list.map((a: any) => (
          <label key={a.id} className="flex cursor-pointer items-center gap-2 border-b px-3 py-1.5 text-sm last:border-0 hover:bg-muted">
            <input type="radio" name="partner" checked={partnerAccountId === a.id} onChange={() => setPartnerAccountId(a.id)} />
            <span className="font-medium">{a.name}</span>
          </label>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-1"><Label>Role</Label><Select value={role} onChange={(e) => setRole(e.target.value)}>{GOV_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}</Select></div>
        <div className="space-y-1"><Label>Workshare %</Label><Input type="number" value={worksharePct} onChange={(e) => setWorksharePct(e.target.value)} /></div>
        <div className="space-y-1"><Label>NDA</Label><Select value={ndaStatus} onChange={(e) => setNdaStatus(e.target.value)}>{NDA_STATUSES.map((n) => <option key={n} value={n}>{n}</option>)}</Select></div>
      </div>
    </BulkModal>
  );
}
